import { Fragment } from 'react'
import { graphql } from 'gatsby'
import { orderBy, chain, zipObject } from 'lodash'
import PageContent from '../static-app/components/PageContent'
import PageTitle from '../shared/PageTitle'
import TabsContainer from '../static-app/components/TabsContainer'
import HeroBanner from '../static-app/components/HeroBanner'
import BasicCarousel from '../static-app/components/BasicCarousel'
import BasicCarouselWithThumbs from '../static-app/components/BasicCarouselWithThumbs'
import ArticleCarousel from '../static-app/components/ArticleCarousel'
import FAQ from '../static-app/components/FAQ'
import TextWithBackground from '../static-app/components/TextWithBackground'
import TextWithFeatureImage from '../static-app/components/TextWithFeatureImage'
import TextImageGrid from '../static-app/components/TextImageGrid'
import ContactForm from '../static-app/components/ContactForm'
import ArticleGridContainer from '../static-app/components/ArticleGridContainer'
import PressCentre from '../static-app/components/PressCentre'
import TabNavigation from '../static-app/components/TabNavigation'
import ProductGrid from '../static-app/components/ProductGrid'
import DynamicGrid from '../static-app/components/DynamicGrid'
import ProductCarousel from '../static-app/components/ProductCarousel'
import AnimationContainer from '../static-app/components/AnimationContainer'
import SignUpBanner from '../static-app/components/SignUpBanner'

export const getComponentForType = type => {
  switch (type) {
    case 'ContentfulGenericPageContent':
      return PageContent
    case 'ContentfulPageTitle':
      return PageTitle
    case 'ContentfulTabsContainer':
      return TabsContainer
    case 'ContentfulPressCentreContainer':
      return PressCentre
    case 'ContentfulHeroBanner':
      return HeroBanner
    case 'ContentfulBasicCarousel':
      return BasicCarousel
    case 'ContentfulBasicCarouselWithThumbs':
      return BasicCarouselWithThumbs
    case 'ContentfulArticleCarousel':
      return ArticleCarousel
    case 'ContentfulAccordionContainer':
      return FAQ
    case 'ContentfulTextWithBackground':
      return TextWithBackground
    case 'ContentfulTextWithFeatureImageContainer':
      return TextWithFeatureImage
    case 'ContentfulTextImageGrid':
      return TextImageGrid
    case 'ContentfulContactForm':
      return ContactForm
    case 'ContentfulArticleGrid':
      return ArticleGridContainer
    case 'ContentfulTabNavigation':
      return TabNavigation
    case 'ContentfulProductGrid':
      return ProductGrid
    case 'ContentfulTextImageWithCtaContainer':
      return DynamicGrid
    case 'ContentfulProductCarousel':
      return ProductCarousel
    case 'ContentfulAnimationBlock':
      return AnimationContainer
    case 'ContentfulSignUpBanner':
      return SignUpBanner
    default:
      return Fragment
  }
}

export const composeValidators = (...validators) => (value, allValues) =>
  validators.reduce((error, validator) => error || validator(value, allValues),
    undefined)

export const required = validationMessage => value =>
  value ? undefined : validationMessage || 'Required'

export const requiredCheckBox = value => {
  return value !== undefined && value.length ? undefined : true
}

export const isEmailValid = validationMessage => email => {
  if (
    email &&
    email.length > 0 &&
    // eslint-disable-next-line max-len
    /^(([^<>()'[\]\\.,;:\s@"]+(\.[^<>()'[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(email) === true
  ) {
    return undefined
  } else return validationMessage || 'Email not valid'
}

export const removeDuplicates = (myArr, prop) => {
  return myArr.filter((
    obj, pos, arr
  ) => {
    return arr.map(mapObj => mapObj[prop]).indexOf(obj[prop]) === pos
  })
}

export const isMobile = () => {
  if (
    typeof window !== `undefined` &&
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
  ) {
    return true
  }
  return false
}

export const scrollToPoint = (
  point, callBackElem, callBackNonElem
) => {
  if (typeof window !== 'undefined') {
    window.onscroll = function() {
      if (point !== null) {
        if (
          document.body.scrollTop > parseInt(point) ||
          document.documentElement.scrollTop > parseInt(point)
        ) {
          callBackElem()
        } else {
          callBackNonElem()
        }
      }
    }
  }
}

/**
 * groupByData
 * @Param: data : it's array of object , groupByItemName: name of the object key to group the items
 * order: sort order by default it's ascending (asc or desc)
 */

export const groupByData = (
  data, groupByItemName, order = 'asc'
) => {
  return orderBy(
    chain(data)
      .groupBy(groupByItemName)
      .toPairs()
      .map(pair => zipObject([groupByItemName, 'data'], pair))
      .value(),
    [groupByItemName],
    [order]
  )
}

export const GenreFragment = graphql`
  fragment GenreFragment on ContentfulGenre {
    id
    title
  }
`

export const ProductCategoryFragment = graphql`
  fragment ProductCategoryFragment on ContentfulProductCategory {
    id
    title
    description {
      internal {
        content
      }
    }
    image {
      fixed(width: 480, quality: 90) {
        src
      }
    }
  }
`

export const TextImageWithCTAFragment = graphql`
  fragment TextImageWithCTAFragment on ContentfulTextImageWithCtaContainer {
    id
    title
    textImageWithCta {
      title
      description {
        childMarkdownRemark {
          html
        }
      }
      image {
        url
        altText
        imageFile {
          file {
            url
          }
        }
      }
      ctaLabel
      url
    }
  }
`

export const isMobileOnly = () => {
  let check

  if (typeof window !== 'undefined') {
    ((a) => {
      if (
        /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(a) ||
        /* eslint-disable-next-line */
        /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0, 4))
      ) { check = true } else { check = false }
    })(navigator.userAgent || navigator.vendor || window.opera)
  } else {
    check = false
  }

  return check
}

export const getImageURL = (image) => {
  let imageUrl
  isMobileOnly() && image.mobileImageFile ? imageUrl = image.mobileImageFile.file.url : imageUrl = image.imageFile.file.url
  return imageUrl
}

export const getFluidImage = (image) => {
  let imageUrl
  isMobileOnly() && image.mobileImageFile ? imageUrl = image.mobileImageFile.fluid : imageUrl = image.imageFile.fluid
  return imageUrl
}

export const encode = data =>
  Object.keys(data)
    .map(key => {
      // Encode properly if boolean
      if (typeof data[key] === 'boolean') {
        // If boolean is true then return yes
        // Otherwise return no
        if (data[key]) {
          return `${encodeURIComponent(key)}=Yes`
        } else {
          return `${encodeURIComponent(key)}=No`
        }
      } else {
        return `${encodeURIComponent(key)}=${encodeURIComponent(data[key])}`
      }
    })
    .join('&')
